import React, { useEffect, useState } from "react";
import {
  Box,
  Button as MuiButton,
  TextField,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import PageHeader1 from "../../components/common/PageHeader1";
import { fetchCreditNotes } from "../../Redux/Reducers/CreditNoteReportReducer";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePermissions } from "../../context/AuthContext";

const CreditReports = () => {
  const { can } = usePermissions();
  const dispatch = useDispatch();

  const {
    data: creditNotes = [],
    paginationDetail = { totalDocs: 0, page: 1, limit: 10 },
    status,
  } = useSelector((state) => state.CreditNoteReportReducer || {});

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Load data on mount and when pagination changes (only if search is not applied)
  useEffect(() => {
    // If search is applied, don't trigger this effect for pagination
    if (appliedSearch) {
      return;
    }
    
    dispatch(
      fetchCreditNotes({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: "",
        startDate: appliedStartDate,
        endDate: appliedEndDate,
      })
    );
  }, [dispatch, paginationModel, appliedStartDate, appliedEndDate, appliedSearch]);

  const handleSearch = () => {
    if (!searchValue.trim()) {
      // If search is empty, reset and load all data
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(
        fetchCreditNotes({
          page: 1,
          limit: 10,
          search: "",
          startDate: appliedStartDate,
          endDate: appliedEndDate,
        })
      );
      return;
    }
    setAppliedSearch(searchValue);
    // Call API with only search parameter (no page/limit)
    dispatch(
      fetchCreditNotes({
        page: 1,
        limit: 10,
        search: searchValue.trim(),
        startDate: "",
        endDate: "",
      })
    );
  };

  // Handle when search input is cleared
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    // If search is cleared (empty), reset and load all data
    if (!value.trim() && appliedSearch) {
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(
        fetchCreditNotes({
          page: 1,
          limit: 10,
          search: "",
          startDate: appliedStartDate,
          endDate: appliedEndDate,
        })
      );
    }
  };

  const handleApplyFilter = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start date and end date");
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedSearch(""); // Clear search when applying date filter
    setPaginationModel({ page: 0, pageSize: 10 }); // Reset to first page when applying filter
    dispatch(
      fetchCreditNotes({
        page: 1,
        limit: 10,
        search: "",
        startDate: startDate,
        endDate: endDate,
      })
    );
  };

  const handleClearFilters = () => {
    setSearchValue("");
    setStartDate("");
    setEndDate("");
    setAppliedSearch("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setPaginationModel({ page: 0, pageSize: 10 });
    dispatch(
      fetchCreditNotes({
        page: 1,
        limit: 10,
        search: "",
        startDate: "",
        endDate: "",
      })
    );
  };

  const mappedRows = (creditNotes || [])
    .filter((item) => !item?.creditNoteFor?.toLowerCase().includes("course"))
    .map((item, index) => ({
      id: item._id || index,
      sono:
        (paginationDetail.page - 1) * paginationDetail.limit + (index + 1),
      creditNoteFor: item.creditNoteFor,
      cancelledInvoice: item.cancelledInvoice,
      segment: item.segment,
      status: item.status || "",
      basicAmount: item.basicAmount,
      gst: item.gst,
      invoiceAmount: item.invoiceAmount,
      createdAt: item.createdAt
        ? new Date(item.createdAt).toLocaleString()
        : "",
      updatedAt: item.updatedAt
        ? new Date(item.updatedAt).toLocaleString()
        : "",
      creditNotePdfUrl: item.creditNotePdfUrl,
    }));

  const handleViewPdf = (pdfUrl) => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } else {
      toast.warn("PDF URL is not available for this record.");
    }
  };

  const columns = [
    { field: "sono", headerName: "S.No.", width: 80 },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="View PDF">
          <IconButton onClick={() => handleViewPdf(row.creditNotePdfUrl)}>
            <i className="icofont-eye text-info"></i>
          </IconButton>
        </Tooltip>
      ),
    },
    { field: "creditNoteFor", headerName: "Credit Note", width: 150 },
    { field: "cancelledInvoice", headerName: "Cancelled Invoice", width: 170 },
    { field: "segment", headerName: "Segment", width: 120 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "basicAmount", headerName: "Basic Amount", width: 130 },
    { field: "gst", headerName: "GST", width: 100 },
    { field: "invoiceAmount", headerName: "Invoice Amount", width: 150 },
    { field: "createdAt", headerName: "Created At", width: 180 },
    { field: "updatedAt", headerName: "Updated At", width: 180 },
  ];

  const handleExport = async () => {
    const token = localStorage.getItem("vedhvidhi-admin-token");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/creditNotes`,
        {
          params: { search: appliedSearch, startDate: appliedStartDate, endDate: appliedEndDate, limit: paginationDetail.totalDocs || 1000 },
          headers: { Authorization: token },
        }
      );

      const exportData = response.data.data.map((row, index) => ({
        "S.No.": index + 1,
        "Credit Note": row.creditNoteFor,
        "Cancelled Invoice": row.cancelledInvoice,
        Segment: row.segment,
        Status: row.status,
        "Basic Amount": row.basicAmount,
        GST: row.gst,
        "Invoice Amount": row.invoiceAmount,
        "Created At": new Date(row.createdAt).toLocaleString(),
        "Updated At": new Date(row.updatedAt).toLocaleString(),
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Credit Reports");

      XLSX.writeFile(
        workbook,
        `CreditReports_${appliedStartDate || "all"}_${appliedEndDate || "all"}.xlsx`
      );
    } catch (err) {
      toast.error("Failed to export Excel file.");
    }
  };

  // Calculate rowCount: use paginationDetail if available, otherwise use data length (for search)
  const rowCount = appliedSearch 
    ? (mappedRows?.length || 0)
    : (paginationDetail?.totalDocs || 0);

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <ToastContainer />
        <PageHeader1 pagetitle="Credit Note Report" />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  {/* Search and Date Filter Section - Inside table card, single line */}
                  <Box display="flex" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
                    <TextField
                      placeholder="Search..."
                      value={searchValue}
                      onChange={handleSearchChange}
                      size="small"
                      sx={{ width: '250px' }}
                    />
                    <IconButton
                      onClick={handleSearch}
                      disabled={status === "loading" || !searchValue.trim()}
                      sx={{ 
                        height: '40px',
                        width: '40px',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '&:disabled': {
                          backgroundColor: 'action.disabledBackground',
                          color: 'action.disabled',
                        }
                      }}
                    >
                      <FaSearch />
                    </IconButton>
                    <TextField
                      type="date"
                      label="Start Date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <TextField
                      type="date"
                      label="End Date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleApplyFilter}
                      disabled={status === "loading"}
                      sx={{ height: '40px' }}
                    >
                      {status === "loading" ? "Loading..." : "Apply"}
                    </Button>
                    <MuiButton variant="contained" color="success" onClick={handleExport} sx={{ height: '40px' }}>
                      Download Excel
                    </MuiButton>
                    <Button 
                      variant="outlined" 
                      color="secondary" 
                      onClick={handleClearFilters}
                      sx={{ height: '40px' }}
                    >
                      Clear
                    </Button>
                  </Box>
                  
                  <Box sx={{ height: 600 }}>
                    <DataGrid
                      rows={mappedRows}
                      columns={columns}
                      getRowId={(row) => row.id}
                      paginationMode={appliedSearch ? "client" : "server"}
                      rowCount={rowCount}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={[5, 10, 25, 50, 100]}
                      loading={status === "loading"}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditReports;